<template>
  <div class="success-step-master-content">
    <p
      class="font-weight-bold"
      v-if="isScheduledPost"
    >
      {{ $t('social-media.alert.schedule-successful.0') }}
    </p>

    <p
      v-if="isScheduledPost"
      class="text-center"
    >
      {{ $t('content-creator.success-step-master-content.post-will-be-published-on') }}
    </p>
    <p
      v-else
      class="text-center"
    >
      {{ sharedOnText }}
    </p>

    <div class="success-step-master-content__statistics">
      <div class="success-step-master-content__platform-icons d-flex gap-1">
        <r-img
          v-for="platform in platforms"
          :key="platform"
          :src="KindTypeIcon[TypeToKind[platform]]"
          width="30"
          height="30"
        />
      </div>
      <div class="success-step-master-content__statistics-item">
        <v-icon>mdi-calendar-month-outline</v-icon>
        <span>
          {{ formatScheduledDate }}
        </span>
      </div>
      <div class="success-step-master-content__statistics-item">
        <v-icon>mdi-calendar-clock</v-icon>
        <span>
          {{ formatScheduledTime }}
        </span>
      </div>
      <div class="success-step-master-content__statistics-item">
        <v-icon>mdi-office-building-outline</v-icon>
        <span>{{ officesCount }}</span>
      </div>
    </div>

    <div
      v-if="notConnectedCount > 0 || failedCount > 0"
      class="success-step-master-content__details mt-2"
    >
      <div
        v-if="notConnectedCount > 0"
        class="success-step-master-content__statistics-item"
      >
        <v-icon color="error">mdi-close-circle</v-icon>
        <span class="text-error">
          {{
            $t('content-creator.success-step-master-content.not-connected', {
              count: notConnectedCount,
            })
          }}
        </span>
      </div>
      <div
        v-if="failedCount > 0"
        class="success-step-master-content__statistics-item"
      >
        <v-icon color="warning">mdi-information-slab-circle</v-icon>
        <span class="text-warning">
          {{ $t('content-creator.success-step-master-content.failed', { count: failedCount }) }}
        </span>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { SocialMediaPost } from '@/generated/graphql'
import { formatDate, getTime } from '@/lib/dateTime'

import { KindTypeIcon, TypeToKind } from '../enums/KindType'

type PublishingSummary = {
  offices: number
  accounts: number
  successful: number
  failed: number
  notConnected: number
  scheduled: number
}

type Props = {
  isScheduledPost: boolean
  scheduledDate: string
  offices: number
  accounts: number
  platforms: string[]
  summary: PublishingSummary | null
  posts: SocialMediaPost[]
}

const props = defineProps<Props>()

const { t } = useI18n()

const formatScheduledDate = computed(() => {
  if (!props.isScheduledPost) return formatDate(new Date(), 'dateLong')
  return formatDate(new Date(props.scheduledDate), 'dateLong')
})

const formatScheduledTime = computed(() => {
  if (!props.isScheduledPost) return getTime(new Date())
  return getTime(new Date(props.scheduledDate))
})

// The batch summary (getMasterBatchPosts, shared with History) is the source of
// truth for the counts. When it is unavailable we fall back to the raw posts so
// the modal still shows sensible numbers.
const successfulPosts = computed(() => props.posts.filter((post) => !post.hasFailed))
const failedPosts = computed(() => props.posts.filter((post) => post.hasFailed))

const officesCount = computed(() => props.summary?.offices ?? props.offices)
const successfulCount = computed(() => props.summary?.successful ?? successfulPosts.value.length)
const failedCount = computed(() => props.summary?.failed ?? failedPosts.value.length)
const notConnectedCount = computed(() => props.summary?.notConnected ?? 0)

const sharedOnText = computed(() => {
  if (successfulCount.value === 0) {
    return t(
      'content-creator.success-step-master-content.failed-to-post-accounts',
      failedCount.value
    )
  }
  return t(
    'content-creator.success-step-master-content.successfully-shared-on-accounts',
    successfulCount.value
  )
})
</script>
<style scoped>
.success-step-master-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.success-step-master-content__statistics {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: center;
}

.success-step-master-content__statistics-item {
  display: flex;
  gap: 6px;
  align-items: center;
}

.success-step-master-content__platform-icons {
  flex-wrap: wrap;
  justify-content: center;
}

.success-step-master-content__details {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  justify-content: center;
}
</style>
